import React, { useCallback, useMemo } from 'react'
import cn from 'classnames'
import { isMobile } from 'lib/device'
import styles from './file-embed.module.scss'
import { getCachedAnswer } from 'state/cache'
import ThreeDModelLoader from './3DModelLoader'
import BsvTransactionStatusIcon from 'components/shared/BsvTransactionStatusIcon'
import AudioAttachmentCard from './AudioAttachmentCard'
import api from 'api/api'
import * as cache from 'state/cache'
import { File as FileIcon } from 'lucide-react'
import { bsvTxExplorerUrl } from 'utils/bsvExplorerUrls'
import {
    deriveMediaDockAnswerArtwork,
    deriveMediaDockAnswerTitle,
} from 'components/shared/MediaDock/mediaDockTrackTitles'
import {
    classifyFileEmbedMedia,
    genericFileDisplay as getGenericFileDisplay,
    tallFileEmbedKind,
} from './fileEmbedMediaClassification'
import OrdinalsRender from './OrdinalsRender'

const SERVER_ATTACHMENT_ID_REGEXP =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function persistedAttachmentId(file) {
    const id = file?.attachment_id || file?.attachmentId || file?.id
    if (typeof id === 'number') return String(id)
    if (typeof id === 'string' && SERVER_ATTACHMENT_ID_REGEXP.test(id)) return id
    if (typeof id === 'string' && /^\d+$/.test(id)) return id
    return null
}

/**
 * @typedef {Object} FileEmbedProps
 * @property {string} [answerId]
 * @property {boolean} [canDelete]
 * @property {any} file
 * @property {boolean} [showDelete]
 * @property {(attachmentId: string, answerId: string) => void} [deleteFile]
 * @property {() => void} [removeFile]
 * @property {number} [processingPercent]
 * @property {(pct: number) => void} [onModelPreviewProgress]
 * @property {(blob: Blob, dataUrl: string, key?: string) => void} [onModelPreviewGenerated]
 * @property {() => void} [onModelPreviewCaptureStart]
 * @property {() => void} [onModelPreviewCaptureEnd]
 * @property {boolean} [autoCaptureModelPreview]
 * @property {any} [panel]
 * @property {boolean} [hover]
 * @property {boolean} [audioAutoPlay]
 * @property {boolean} [audioIsPaused]
 * @property {string | number} [audioResumePlaybackToken]
 * @property {(duration: number) => void} [audioOnDurationChange]
 * @property {(currentTime: number, duration: number) => void} [audioOnProgressChange]
 * @property {() => void} [audioOnPlaybackError]
 * @property {(isPlaying: boolean) => void} [audioOnPlaybackStateChange]
 * @property {(isPlaying: boolean) => void} [audioOnManualPlaybackChange]
 * @property {(isDockOwned: boolean) => void} [audioOnDockOwnedChange]
 * @property {'default' | 'media' | 'play' | 'scrim'} [audioTheme]
 * @property {boolean} [audioArtwork]
 * @property {boolean} [audioPersistPlayback]
 * @property {string} [audioPlaybackKey]
 * @property {boolean} [audioPlayerCompact]
 * @property {boolean} [audioVolumeFlyout]
 * @property {boolean} [audioBackgroundClickPassthrough]
 * @property {string} [audioPlayerClassName]
 * @property {string} [audioPlayerContentClassName]
 * @property {boolean} [showBsvStatus]
 */

/**
 * @param {FileEmbedProps} props
 */
function FileEmbedInner(props) {
    const {
        answerId,
        canDelete,
        file,
        showDelete,
        deleteFile: propsDeleteFile,
        removeFile,
        processingPercent,
        onModelPreviewProgress,
        onModelPreviewGenerated,
        onModelPreviewCaptureStart,
        onModelPreviewCaptureEnd,
        autoCaptureModelPreview,
        audioAutoPlay,
        audioIsPaused,
        audioResumePlaybackToken,
        audioOnDurationChange,
        audioOnProgressChange,
        audioOnPlaybackError,
        audioOnPlaybackStateChange,
        audioOnManualPlaybackChange,
        audioOnDockOwnedChange,
        audioTheme,
        audioArtwork,
        audioPersistPlayback,
        audioPlaybackKey,
        audioPlayerCompact,
        audioVolumeFlyout = false,
        audioBackgroundClickPassthrough = false,
        audioPlayerClassName,
        audioPlayerContentClassName,
        showBsvStatus = true,
    } = props

    const answer = getCachedAnswer(answerId)
    const walletPost = useMemo(
        () => (answer?.id ? { answerId: answer.id, threadId: answer.quest_id || null } : null),
        [answer?.id, answer?.quest_id],
    )
    const mediaDockTrackTitle = useMemo(() => deriveMediaDockAnswerTitle(answer), [answer])
    const mediaDockTrackArtwork = useMemo(() => deriveMediaDockAnswerArtwork(answer), [answer])

    // Find matching BSV attachment
    const matchingBsvAttachment = useMemo(
        () => answer?.bsv_attachments?.find(att => att.attachment_id === file.id),
        [answer?.bsv_attachments, file.id],
    )
    const displayedBsvAttachment = showBsvStatus ? matchingBsvAttachment : null

    // Render BSV attachment status
    const renderBsvAttachmentStatus = useCallback(matchingBsvAttachment => {
        const bsvTx = matchingBsvAttachment?.bsv_tx
        const txId = bsvTx?.tx_id
        return (
            <BsvTransactionStatusIcon
                bsvTx={bsvTx}
                fallbackTxId={txId}
                wrapperProps={{
                    href: bsvTxExplorerUrl(txId),
                    target: txId ? '_blank' : undefined,
                    rel: txId ? 'noopener noreferrer' : undefined,
                    onClick: ev => ev.stopPropagation(),
                }}
            />
        )
    }, [])

    // Memoize the delete handler
    const deleteFile = useCallback(
        attachmentId => {
            if (attachmentId && answerId && canDelete) {
                propsDeleteFile(attachmentId, answerId)
            } else {
                removeFile()
            }
        },
        [answerId, canDelete, propsDeleteFile, removeFile],
    )

    // Memoize event handler
    const preventPropagation = useCallback(ev => {
        ev.stopPropagation()
        ev.preventDefault()
        return false
    }, [])

    const stopPropagationOnly = useCallback(ev => {
        ev.stopPropagation()
    }, [])

    // Memoize file type checks using MIME type with extension fallback for generic uploads
    const fileTypes = useMemo(() => {
        return classifyFileEmbedMedia(file)
    }, [
        file.content_type,
        file.contentType,
        file.mime,
        file.type,
        file.name,
        file.filename,
        file.original_filename,
        file.url,
        file.file_url,
    ])

    const genericFileDisplay = useMemo(() => {
        return getGenericFileDisplay(file)
    }, [
        file.content_type,
        file.contentType,
        file.mime,
        file.type,
        file.filename,
        file.name,
        file.original_filename,
    ])

    const handleDownloadClick = useCallback(
        e => {
            stopPropagationOnly(e)
            if (isMobile) return

            e.preventDefault()
            fetch(file.url)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`Download failed: ${response.status}`)
                    }
                    return response.blob()
                })
                .then(blob => {
                    const url = window.URL.createObjectURL(blob)
                    const link = document.createElement('a')
                    link.href = url
                    link.download = genericFileDisplay.displayName
                    link.click()
                    window.URL.revokeObjectURL(url)
                })
                .catch(error => {
                    console.error('Failed to download file:', error)
                    window.open(file.url, '_blank', 'noopener,noreferrer')
                })
        },
        [file.url, genericFileDisplay.displayName, stopPropagationOnly],
    )

    const handlePreviewGenerated = useCallback(
        async (blob, dataUrl, key = '1x1') => {
            if (onModelPreviewGenerated) return onModelPreviewGenerated(blob, dataUrl, key)

            const attachmentId = persistedAttachmentId(file)
            if (!attachmentId) return

            try {
                await api.updateFilePreview(attachmentId, blob, key)
                if (answerId) {
                    const cur = getCachedAnswer(answerId)
                    const curUrls = Array.isArray(cur?.file_urls) ? cur.file_urls : []
                    const newUrls = curUrls.map(u => {
                        if (
                            !(
                                u?.id === attachmentId ||
                                u?.attachment_id === attachmentId ||
                                u?.['attachmentId'] === attachmentId
                            )
                        )
                            return u
                        const nextPreviewUrls = { ...(u.preview_urls || {}) }
                        nextPreviewUrls[key] = dataUrl
                        const fallback = nextPreviewUrls['1x1'] || u.preview_url
                        return { ...u, preview_urls: nextPreviewUrls, preview_url: fallback }
                    })
                    cache.updateCachedAnswer(answerId, { file_urls: newUrls })
                }
            } catch (e) {
                console.warn('Failed to upload generated preview via updateFilePreview:', e)
            }
        },
        [onModelPreviewGenerated, answerId, file],
    )

    const localPreferredUrl = useMemo(() => {
        // Only use local blob fallback for freshly posted (local) answers
        if (!answer?.isLocal) return null
        try {
            return localStorage.getItem(`local3d:${answerId}:${file?.name}`)
        } catch (e) {
            return null
        }
    }, [answerId, file?.name, answer?.isLocal])

    const canPersistGeneratedModelPreview = Boolean(
        onModelPreviewGenerated || persistedAttachmentId(file),
    )
    const shouldAutoCaptureModelPreview = Boolean(
        canPersistGeneratedModelPreview && (autoCaptureModelPreview || answer?.isLocal),
    )

    const genericFileEmbed = (
        <div className={styles.downloadBtn}>
            <a
                className={styles.downloadLink}
                href={file.url}
                download={genericFileDisplay.displayName}
                onClick={handleDownloadClick}
                onMouseDown={stopPropagationOnly}
                onMouseUp={stopPropagationOnly}
                onTouchStart={stopPropagationOnly}
                onTouchEnd={stopPropagationOnly}
                onDrag={stopPropagationOnly}
                onDragStart={stopPropagationOnly}
            >
                <span className={styles.icon} aria-hidden="true">
                    <FileIcon size={20} strokeWidth={2} />
                </span>
                <span className={styles.fileText}>
                    <span className={styles.name}>{genericFileDisplay.displayName}</span>
                    <span className={styles.fileMeta}>{genericFileDisplay.typeLabel}</span>
                </span>
            </a>

            {displayedBsvAttachment && (
                <div className={styles.bsvStatusOverlay}>
                    {renderBsvAttachmentStatus(displayedBsvAttachment)}
                </div>
            )}

            {showDelete && (
                <div className={styles.delete}>
                    <i className="fa fa-close" onClick={() => deleteFile(file.id)} />
                </div>
            )}
        </div>
    )

    return (
        <div
            className={cn(styles.fileEmbed)}
            data-testid="answer-file-embed"
            // FileEmbed has a second click boundary outside the audio-specific wrapper below.
            // Release it as well or the combined transport's background click still dies here.
            onClick={audioBackgroundClickPassthrough ? undefined : preventPropagation}
            onMouseDown={preventPropagation}
            onMouseUp={preventPropagation}
            onTouchStart={preventPropagation}
            onTouchEnd={preventPropagation}
            onDrag={preventPropagation}
            onDragStart={preventPropagation}
        >
            {tallFileEmbedKind(file) === 'htmlOrdinal' ? (
                // Same render the wallet gives HTML ordinals: a screenshot (or a play placeholder
                // while none exists yet) until played, then the inscription in its sandboxed
                // iframe. Not the stream grid's passive mode, which leaves the frame blank
                // until a screenshot lands, and a fresh mint has none.
                <div className={styles.htmlOrdinalViewer} data-testid="answer-file-html-ordinal">
                    <OrdinalsRender
                        outpoint={file.bsv_ordinal.origin || ''}
                        url={`${window.knovApiUrl}/api/v1/bsv/ordinals/${file.bsv_ordinal.id}/data`}
                        bsvOrdinalId={file.bsv_ordinal.id}
                        artifactOrdinalId={file.bsv_ordinal.id}
                        walletPost={walletPost}
                        contentType="text/html"
                        allowInteraction={true}
                        htmlAlign="center"
                        title={genericFileDisplay.displayName}
                        filename={genericFileDisplay.displayName}
                    />
                </div>
            ) : fileTypes.is3DModel ? (
                <div className={styles.modelViewer}>
                    <ThreeDModelLoader
                        file={file}
                        onPreviewProgress={onModelPreviewProgress}
                        onPreviewGenerated={
                            canPersistGeneratedModelPreview ? handlePreviewGenerated : undefined
                        }
                        onPreviewCaptureStart={onModelPreviewCaptureStart}
                        onPreviewCaptureEnd={onModelPreviewCaptureEnd}
                        localUrl={localPreferredUrl || undefined}
                        answerId={answerId}
                        autoCapturePreview={shouldAutoCaptureModelPreview}
                    />

                    {typeof processingPercent === 'number' && processingPercent < 100 && (
                        <div className={styles.processingBar}>
                            <div
                                className={styles.fill}
                                style={{
                                    width: `${Math.max(0, Math.min(100, processingPercent))}%`,
                                }}
                            />
                        </div>
                    )}

                    {showDelete && (
                        <div className={styles.delete}>
                            <i className="fa fa-close" onClick={() => deleteFile(file.id)} />
                        </div>
                    )}
                </div>
            ) : fileTypes.isAudio ? (
                <div
                    className={cn(
                        styles.audioPlayer,
                        audioPlayerCompact && styles.audioPlayerCompact,
                        audioPlayerClassName,
                    )}
                    data-no-modal-interaction
                    // Only reached by clicks the player root released — the inert gaps
                    // between its controls — so this wrapper releases them too.
                    onClick={audioBackgroundClickPassthrough ? undefined : stopPropagationOnly}
                    onMouseDown={stopPropagationOnly}
                    onMouseUp={stopPropagationOnly}
                    onPointerDown={stopPropagationOnly}
                    onTouchStart={stopPropagationOnly}
                    onTouchEnd={stopPropagationOnly}
                    onDrag={preventPropagation}
                    onDragStart={preventPropagation}
                    onDragOver={preventPropagation}
                    onDragEnter={preventPropagation}
                    onDragLeave={preventPropagation}
                    onDrop={preventPropagation}
                >
                    <AudioAttachmentCard
                        key={audioPlaybackKey || file.id || file.attachment_id || file.url}
                        src={file.url}
                        fileName={file.name || file.filename || file.original_filename}
                        knownDuration={file.duration}
                        trackTitle={mediaDockTrackTitle || undefined}
                        posterSrc={file.preview_url || mediaDockTrackArtwork || undefined}
                        answerId={answerId}
                        questId={answer?.quest_id}
                        audioSourceType={fileTypes.audioSourceType}
                        theme={audioTheme}
                        artwork={audioArtwork}
                        compact={audioPlayerCompact}
                        volumeFlyout={audioVolumeFlyout}
                        backgroundClickPassthrough={audioBackgroundClickPassthrough}
                        className={audioPlayerContentClassName}
                        autoPlay={audioAutoPlay}
                        isPaused={audioIsPaused}
                        persistPlayback={audioPersistPlayback}
                        playbackKey={audioPlaybackKey || file.id || file.attachment_id}
                        resumePlaybackToken={audioResumePlaybackToken}
                        onDurationChange={audioOnDurationChange}
                        onProgressChange={audioOnProgressChange}
                        onPlaybackError={audioOnPlaybackError}
                        onPlaybackStateChange={audioOnPlaybackStateChange}
                        onManualPlaybackChange={audioOnManualPlaybackChange}
                        onDockOwnedChange={audioOnDockOwnedChange}
                        matchingBsvAttachment={displayedBsvAttachment}
                        renderBsvAttachmentStatus={renderBsvAttachmentStatus}
                        showDelete={showDelete}
                        onDelete={() => deleteFile(file.id)}
                        statusOverlayClassName={cn(
                            styles.bsvStatusOverlay,
                            styles.audioStatusOverlay,
                        )}
                        statusOverlayWithDeleteClassName={styles.audioStatusOverlayWithDelete}
                        deleteClassName={cn(styles.delete, styles.audioDelete)}
                        stopPropagationOnly={stopPropagationOnly}
                        preventPropagation={preventPropagation}
                    />
                </div>
            ) : (
                genericFileEmbed
            )}
        </div>
    )
}

const FileEmbed = React.memo(FileEmbedInner)

export default FileEmbed
